import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  IconButton,
  Icon,
  Spinner,
  Center,
  Img,
  Flex,
  Box,
} from '@chakra-ui/react';
import { useEffect, useState, useMemo } from 'react';
import { MdEdit, MdOutlineDelete } from 'react-icons/md';
import { FaCaretUp, FaCaretDown } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import * as api from '../../../../api/midiaDigital';
import Swal from 'sweetalert2';
import CustomAudioPlayer from '../../../../components/audioPlayer/CustomAudioPlayer';
import './tabela-biblioteca-midia-digital.scss';

export interface TipoMidia {
  codMidia: number;
  dscMidia: string;
}

export interface BibliotecaMidiaDigital {
  seqMidia: number;
  codMidia: number | string;
  dscUriCdn: string;
  dscMidia: string;
  datRegistro: string;
  datPublicacao: string;
  datDespublicar: string;
  tipoMidia?: TipoMidia;
}

interface TabelaBibliotecaMidiaDigitalProps {
  listaBibliotecaMidiaDigital: BibliotecaMidiaDigital[];
  listaVazia: string;
  loading: boolean;
  onDelete: (seqMidia: number) => void; 
}

type Ordenacao = { chave: keyof BibliotecaMidiaDigital; direcao: 'asc' | 'desc' } | null;

const TabelaBibliotecaMidiaDigital = ({
  listaBibliotecaMidiaDigital,
  listaVazia,
  loading,
  onDelete
}: TabelaBibliotecaMidiaDigitalProps) => {
  const [tipoMidia, setTipoMidia] = useState<TipoMidia[]>([]);
  const [ordenacao, setOrdenacao] = useState<Ordenacao>(null);
  const [colunaDestacada, setColunaDestacada] = useState<keyof BibliotecaMidiaDigital | null>(null);
  const navigate = useNavigate();

  function deletarPublicacao(seqPublicacao: number) {
    Swal.fire({
      title: 'Tem certeza?',
      text: "Certeza que deseja deletar esta publicação?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#1789FC',
      cancelButtonColor: '#EDF2F7',
      confirmButtonText: 'Sim, deletar',
      cancelButtonText: 'Cancelar'
    }).then(async (result) => {
      if (result.isConfirmed) {
        Swal.fire({
          icon: 'info',
          title: 'Aguarde',
          allowOutsideClick: false,
          text: 'Deletando...',
          showConfirmButton: false,
      });
        try {
          const obterMidia = await api.obterMidia(seqPublicacao);
          if (obterMidia.dscUriCdn) {
            const delimiter = "/cms-dev-files/";
            const index = obterMidia.dscUriCdn.lastIndexOf(delimiter);
            const stringFinalDaUrlCdn =
              index !== -1 ? obterMidia.dscUriCdn.substring(index + delimiter.length) : obterMidia.dscUriCdn;
            
            await api.deletarNoCdn(stringFinalDaUrlCdn);
          }
        } catch (error) {
          console.error("Erro ao obter publicação:", error);
        }
        try {
          await api.deletarMidiaDigital(seqPublicacao).then(() => {
            onDelete(seqPublicacao);
            Swal.fire({
              icon: 'success',
              title: 'Sucesso!',
              text: 'Publicação deletada com sucesso.',
              confirmButtonColor: '#1789FC',
              confirmButtonText: 'OK',
            })
          });
        } catch (error) {
          console.error('Erro ao deletar publicação:', error);
          Swal.fire({
            icon: 'error',
            title: 'Ocorreu um erro!',
            text: 'Erro ao deletar publicação. Tente novamente mais tarde.',
            confirmButtonColor: '#1789FC',
            confirmButtonText: 'OK',
          }).then(() => {
            navigate(-1)
          });
        }
      }
    });
  }  

  useEffect(() => {
    const fetchTipoMidia = async () => {
      try {
        const result = await api.listarTipoDeMidia();
        setTipoMidia(result);
      } catch (error) {
        console.error('Erro ao buscar tipos de mídia:', error);
      }
    };
    fetchTipoMidia();
  }, []);

  const getTipoDeMidia = (midia: BibliotecaMidiaDigital) => {
    const source = midia.dscUriCdn || midia.dscMidia;
    const fileName = decodeURIComponent(source).toLowerCase();
    if (fileName.endsWith('.mp4')) return 'video';
    if (
      fileName.endsWith('.mp3') ||
      fileName.endsWith('.wav') ||
      fileName.endsWith('.ogg')
    )
      return 'audio';
    if (
      fileName.endsWith('.jpg') ||
      fileName.endsWith('.jpeg') ||
      fileName.endsWith('.png') ||
      fileName.endsWith('.gif') ||
      fileName.endsWith('.bmp')
    )
      return 'image';
    return null;
  };

  const corrigirString = (url: string): string => {
    const decoded = decodeURIComponent(url);
    const parts = decoded.split('/');
    const fileNameWithExt = parts[parts.length - 1];
    const indexDot = fileNameWithExt.lastIndexOf('.');
    const withoutExtension = indexDot !== -1 ? fileNameWithExt.substring(0, indexDot) : fileNameWithExt;
    const underscoreParts = withoutExtension.split('_');
    return underscoreParts.length > 1 ? underscoreParts[1] : underscoreParts[0];
  };

  const atualizarPreview = (url: string) => {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}t=${new Date().getTime()}`;
  };

  const manipularOrdenacao = (chave: keyof BibliotecaMidiaDigital) => {
    let direcao: 'asc' | 'desc' = 'asc';
    if (ordenacao && ordenacao.chave === chave && ordenacao.direcao === 'asc') {
      direcao = 'desc';
    }
    setOrdenacao({ chave, direcao });
  };

  const listaOrdenada = useMemo(() => {
    const copia = [...listaBibliotecaMidiaDigital];
    if (ordenacao !== null) {
      copia.sort((a, b) => {
        let valorA: any, valorB: any;
        if (ordenacao.chave === 'tipoMidia') {
          valorA = a.tipoMidia?.dscMidia || '';
          valorB = b.tipoMidia?.dscMidia || '';
        } else {
          valorA = a[ordenacao.chave];
          valorB = b[ordenacao.chave];
        }
        if (typeof valorA === 'string' && typeof valorB === 'string') {
          const comparacao = valorA.localeCompare(valorB);
          return ordenacao.direcao === 'asc' ? comparacao : -comparacao;
        }
        if (valorA < valorB) return ordenacao.direcao === 'asc' ? -1 : 1;
        if (valorA > valorB) return ordenacao.direcao === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return copia;
  }, [listaBibliotecaMidiaDigital, ordenacao]);

  const obterIndicadorOrdenacao = (chave: keyof BibliotecaMidiaDigital) => {
    if (ordenacao && ordenacao.chave === chave) {
      return ordenacao.direcao === 'asc' ? (
        <FaCaretUp color="#1789FC" style={{ marginLeft: '4px' }} />
      ) : (
        <FaCaretDown color="#1789FC" style={{ marginLeft: '4px' }} />
      );
    }
      return <FaCaretUp color="#4a5568" style={{ marginLeft: '4px' }} />;
  };

  const renderizarLinhas = () =>
    listaOrdenada && listaOrdenada.length > 0 ? (
      listaOrdenada.map((midia) => {
        const mediaType = getTipoDeMidia(midia);
        return (
          <Tr key={midia.seqMidia}>
            <Td>{midia.seqMidia}</Td>
            <Td maxW="250px" isTruncated>{midia.dscMidia}</Td>
            <Td>{midia.tipoMidia?.dscMidia}</Td>
            <Td>
              {mediaType === 'image' ? (
                <Img
                  src={atualizarPreview(midia.dscUriCdn)}
                  className="imagem-padrao"
                  alt={corrigirString(midia.dscUriCdn)}
                />
              ) : mediaType === 'video' ? (
                <video className="video-padrao" controls>
                  <source src={atualizarPreview(midia.dscUriCdn)} type="video/mp4" />
                  Seu navegador não suporta a tag de vídeo.
                </video>
              ) : mediaType === 'audio' ? (
                <Flex align="center" justify="center" height="100px" width="150px">
                  <CustomAudioPlayer src={atualizarPreview(midia.dscUriCdn)} />
                </Flex>
              ) : null}
            </Td>
            <Td>{midia.datRegistro.split('-').reverse().join('/')}</Td>
            <Td>
              <IconButton
                aria-label="Editar mídia"
                bgColor="#fff"
                onClick={() => navigate(`editar-midia/${midia.seqMidia}`)}
              >
                <MdEdit fontSize="24px" color="#1789FC" />
              </IconButton>
            </Td>
            <Td>
            <IconButton
              aria-label="Botão deletar"
              bgColor="#fff"
              _hover={{ bg: '#ff00002b' }}
              onClick={() => deletarPublicacao(midia.seqMidia)}
            >
              <Icon as={MdOutlineDelete} fontSize="24px" color="red"/>
            </IconButton>
            </Td>
          </Tr>
        );
      })
    ) : (
      <Tr>
        <Td colSpan={6} color="#999898" textAlign="center">
          {listaVazia}
        </Td>
      </Tr>
    );

  if (loading) {
    return (
      <Center mt="20">
        <Spinner size="lg" color="blue.500" />
      </Center>
    );
  }

  return (
    <TableContainer>
      <Table variant="simple">
        <Thead>
          <Tr>
            <Th
              _hover={{ transition: '0.1s', color: 'blue.500' }}
              onClick={() => manipularOrdenacao('seqMidia')}
              onMouseEnter={() => setColunaDestacada('seqMidia')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
              color={ordenacao?.chave === 'seqMidia' ? 'blue.500' : '#4a5568'}
            >
              <Box display="flex" alignItems="center">
                ID {obterIndicadorOrdenacao('seqMidia')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: 'blue.500' }}
              onClick={() => manipularOrdenacao('dscMidia')}
              onMouseEnter={() => setColunaDestacada('dscMidia')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
              color={ordenacao?.chave === 'dscMidia' ? 'blue.500' : '#4a5568'}
            >
              <Box display="flex" alignItems="center">
                Nome da Mídia {obterIndicadorOrdenacao('dscMidia')}
              </Box>
            </Th>
            <Th
              _hover={{ transition: '0.1s', color: 'blue.500' }}
              onClick={() => manipularOrdenacao('tipoMidia')}
              onMouseEnter={() => setColunaDestacada('tipoMidia')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
              color={ordenacao?.chave === 'tipoMidia' ? 'blue.500' : '#4a5568'}
            >
              <Box display="flex" alignItems="center">
                Tipo de Mídia {obterIndicadorOrdenacao('tipoMidia')}
              </Box>
            </Th>
            <Th>Preview da Mídia</Th>
            <Th
              _hover={{ transition: '0.1s', color: 'blue.500' }}
              onClick={() => manipularOrdenacao('datRegistro')}
              onMouseEnter={() => setColunaDestacada('datRegistro')}
              onMouseLeave={() => setColunaDestacada(null)}
              cursor="pointer"
              color={ordenacao?.chave === 'datRegistro' ? 'blue.500' : '#4a5568'}
            >
              <Box display="flex" alignItems="center">
                Data do Registro {obterIndicadorOrdenacao('datRegistro')}
              </Box>
            </Th>
            <Th>Editar</Th>
            <Th>Deletar</Th>
          </Tr>
        </Thead>
        <Tbody>{renderizarLinhas()}</Tbody>
      </Table>
    </TableContainer>
  );
};

export default TabelaBibliotecaMidiaDigital;
